import { type ComponentProps, type ReactNode } from 'react'

import { SETTINGS_ITEM_ATTR } from '@/components/dialog/settings/settings-card-styles'
import { cn } from '@/lib/utils'

interface SettingsCardProps extends ComponentProps<'div'> {
  /** `row`: header on the left, one action on the right. `stack`: header then content below. */
  layout?: 'row' | 'stack'
}

/**
 * One section of a Settings tab. No frame of its own: Settings already sits in the dialog (or
 * the page), so sections are told apart by a hairline between them, like the rows inside them,
 * rather than each being a box within the box.
 */
export function SettingsCard({ layout = 'stack', className, ...props }: SettingsCardProps) {
  return (
    <div
      // A row section rises in as one block; a stack section lets its rows do it.
      {...(layout === 'row' ? SETTINGS_ITEM_ATTR : {})}
      className={cn(
        'border-foreground/10 flex w-full border-b py-6 first:pt-0 last:border-b-0 last:pb-0',
        layout === 'row' ? 'items-center justify-between gap-4' : 'flex-col gap-3',
        className
      )}
      {...props}
    />
  )
}

interface SettingsCardHeaderProps {
  title: ReactNode
  description?: ReactNode
  /** Rendered before the title, e.g. a venue/brand glyph. */
  icon?: ReactNode
  className?: string
}

/** Title + muted description block at the top (or left, in `row` cards) of a SettingsCard. */
export function SettingsCardHeader({
  title,
  description,
  icon,
  className,
}: SettingsCardHeaderProps) {
  const heading = <h2 className="text-foreground text-sm font-semibold tracking-tight">{title}</h2>
  return (
    <div {...SETTINGS_ITEM_ATTR} className={cn('flex flex-col gap-1', className)}>
      {icon ? (
        <div className="flex items-center gap-2">
          {icon}
          {heading}
        </div>
      ) : (
        heading
      )}
      {description && (
        <p className="text-foreground/60 w-full max-w-100 text-sm tracking-tight">{description}</p>
      )}
    </div>
  )
}

interface SettingsRowProps {
  label: ReactNode
  description?: ReactNode
  /** The row's input — a Switch, Select, Button… — right-aligned. */
  control: ReactNode
  className?: string
}

/**
 * The one settings row: label (+ optional muted description) on the left, its control on
 * the right. Stack rows in a `SettingsRowGroup` so they share dividers and spacing.
 */
export function SettingsRow({ label, description, control, className }: SettingsRowProps) {
  return (
    <div
      {...SETTINGS_ITEM_ATTR}
      className={cn('flex w-full items-center justify-between gap-4 py-3', className)}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-foreground text-sm font-medium tracking-tight">{label}</span>
        {description && <p className="text-foreground/60 text-xs tracking-tight">{description}</p>}
      </div>
      <div className="flex shrink-0 items-center">{control}</div>
    </div>
  )
}

/** Divided column of `SettingsRow`s; the first/last rows sit flush with the section. */
export function SettingsRowGroup({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'divide-foreground/10 flex w-full flex-col divide-y [&>*:first-child]:pt-0 [&>*:last-child]:pb-0',
        className
      )}
      {...props}
    />
  )
}
